import React from 'react';
import { Info } from 'lucide-react';
import type { HeadContentData } from './types';
import { ImageUrlWithUpload } from '../ImageUrlWithUpload';

interface SocialMediaTabProps {
  formData: HeadContentData;
  onInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;
  /** Site ID for media library access */
  siteId?: string;
  /** Site-wide Social Image URL. Used when the page OG field is blank. */
  siteOgImage?: string;
}

export const SocialMediaTab: React.FC<SocialMediaTabProps> = ({
  formData,
  onInputChange,
  siteId,
  siteOgImage,
}) => {
  const pageOgBlank = !formData.ogImage?.trim();
  const inheritedOg = siteOgImage?.trim() || '';

  return (
    <div className="space-y-6">
      {/* Open Graph */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <Info className="w-4 h-4 mr-2" />
          Open Graph (Facebook, LinkedIn)
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OG Title</label>
            <input
              type="text"
              name="ogTitle"
              value={formData.ogTitle}
              onChange={onInputChange}
              placeholder="Title for social media sharing"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OG Description</label>
            <textarea
              name="ogDescription"
              value={formData.ogDescription}
              onChange={onInputChange}
              placeholder="Description for social media sharing"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {siteId ? (
            <ImageUrlWithUpload
              value={formData.ogImage}
              onChange={url =>
                onInputChange({
                  target: { name: 'ogImage', value: url },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              siteId={siteId}
              label="OG Image"
              placeholder="https://example.com/image.jpg"
              recommendedSize="1200x630px"
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">OG Image URL</label>
              <input
                type="url"
                name="ogImage"
                value={formData.ogImage}
                onChange={onInputChange}
                placeholder="https://example.com/image.jpg (1200x630px recommended)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {pageOgBlank && inheritedOg ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">Using site social image.</p>
              <div className="w-40 h-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                <img
                  src={inheritedOg}
                  alt="Site social image"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          ) : null}

          {pageOgBlank && !inheritedOg ? (
            <p className="text-sm text-gray-600">
              No site social image set. Add one in Site SEO and Analytics, or upload a page-specific
              image here.
            </p>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OG Type</label>
            <select
              name="ogType"
              value={formData.ogType}
              onChange={onInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="website">Website</option>
              <option value="article">Article</option>
              <option value="product">Product</option>
              <option value="profile">Profile</option>
            </select>
          </div>
        </div>
      </div>

      {/* Twitter */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <Info className="w-4 h-4 mr-2" />
          Twitter Cards
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card Type</label>
            <select
              name="twitterCard"
              value={formData.twitterCard}
              onChange={onInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="summary">Summary</option>
              <option value="summary_large_image">Summary Large Image</option>
              <option value="app">App</option>
              <option value="player">Player</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Twitter Title</label>
            <input
              type="text"
              name="twitterTitle"
              value={formData.twitterTitle}
              onChange={onInputChange}
              placeholder="Title for Twitter cards"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Twitter Description
            </label>
            <textarea
              name="twitterDescription"
              value={formData.twitterDescription}
              onChange={onInputChange}
              placeholder="Description for Twitter cards"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {siteId ? (
            <ImageUrlWithUpload
              value={formData.twitterImage}
              onChange={url =>
                onInputChange({
                  target: { name: 'twitterImage', value: url },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              siteId={siteId}
              label="Twitter Image"
              placeholder="https://example.com/twitter-image.jpg"
              recommendedSize="1200x600px"
            />
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Twitter Image URL
              </label>
              <input
                type="url"
                name="twitterImage"
                value={formData.twitterImage}
                onChange={onInputChange}
                placeholder="https://example.com/twitter-image.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
